import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { BlockTextbox } from '@src/app/@shared/models/step.model';
import { AbstractControl, FormBuilder, FormControl, Validators } from '@angular/forms';
import { BlockAbstractComponent } from '@app/components/blocks/block.abstract.component';
import { PlInputType } from '@app/components/pl-input/pl-input.component';
import { plValidateEmail } from '@app/components/pl-input/email-validator';

export enum FormControlName {
  input = 'input',
}

interface TextboxForm {
  [FormControlName.input]: FormControl<string>;
}

@Component({
  selector: 'pl-block-textbox',
  templateUrl: './block-textbox.component.html',
})
export class BlockTextboxComponent extends BlockAbstractComponent<BlockTextbox, TextboxForm> {
  protected FormControlName: typeof FormControlName = FormControlName;

  @Input() public invalidatePatternValidationIfEmojiInValue: boolean = false;

  @ViewChild('plInput', { read: ElementRef }) public plInputElement!: ElementRef;

  protected override initForm(): void {
    const fb: FormBuilder = new FormBuilder();
    this.form = fb.nonNullable.group<TextboxForm>({
      input: fb.nonNullable.control(''),
    });
  }

  protected override patchFormWithEmptyValue(): void {
    if (this.form) {
      this.form.patchValue(
        {
          [FormControlName.input]: '',
        },
        { emitEvent: false },
      );
    } else {
      throw new Error(`patchFormWithEmptyValue() this.form should not be null when this function is called`);
    }
  }

  protected override patchFormFromBlock(block: BlockTextbox, valueFromLocalStorage: string | null): void {
    if (this.form) {
      let value: string | null = block.data.value;
      let markFormAsDirtyAndEmitValue: boolean = false;
      if (!value && valueFromLocalStorage) {
        value = valueFromLocalStorage;
        this.form.controls.input.markAsDirty();
        markFormAsDirtyAndEmitValue = true;
      }

      this.form.patchValue(
        {
          input: value || '',
        },
        { emitEvent: markFormAsDirtyAndEmitValue },
      );
      if (block.data.config.required === '1') {
        this.form.controls.input.addValidators(Validators.required);
      } else {
        this.form.controls.input.removeValidators(Validators.required);
      }

      if (block.data.config.type === PlInputType.email) {
        this.form.controls.input.addValidators(plValidateEmail);
      } else {
        this.form.controls.input.removeValidators(plValidateEmail);
      }
    } else {
      throw new Error(`patchFormFromBlock() this.form should not be null when this function is called`);
    }
  }

  protected override getUpdatedBlock(): BlockTextbox {
    if (this.form && this.block) {
      const control: AbstractControl | null = this.form.get(FormControlName.input);
      if (control) {
        this.courseContentService.saveShortCodesInLocalStorage({ [this.block.program_data_code]: control.value });
        return {
          ...this.block,
          data: {
            ...this.block.data,
            value: control.value,
          },
        };
      }
    }

    throw new Error(`getUpdatedBlock() this.form or control should not be null when this function is called`);
  }

  protected override onProgrammaticFocus(): void {
    // preventScroll: true because scroll is handled by directive plScrollToInvalidControlOnSubmit
    this.plInputElement.nativeElement.focus({ preventScroll: true });
  }
}
